import type {
    AnimatedIconNode
} from "./type";

type Point = [number, number];

type PathReader = {
    source: string;
    index: number;
};

const PATH_NUMBER_PATTERN = /[+-]?(?:\d+\.?\d*|\.\d+)(?:[eE][+-]?\d+)?/y;

const PATH_SEPARATOR_PATTERN = /[\s,]/;

const PATH_COMMAND_PATTERN = /[a-zA-Z]/;

const PATH_PARAM_COUNTS: Record<string, number> = {
    a: 7,
    c: 6,
    h: 1,
    l: 2,
    m: 2,
    q: 4,
    s: 4,
    t: 2,
    v: 1,
    z: 0
};

const PATH_ARC_FLAG_INDEXES: Array<number> = [
    3,
    4
];

const PATH_CURVE_SAMPLE_COUNT = 32;

const PATH_ARC_MIN_SAMPLE_COUNT = 8;

const getDistance = (from: Point, to: Point): number => {
    return Math.hypot(to[0] - from[0], to[1] - from[1]);
};

const skipSeparators = (reader: PathReader) => {
    while(reader.index < reader.source.length && PATH_SEPARATOR_PATTERN.test(reader.source.charAt(reader.index))) {
        reader.index += 1;
    }
};

const readNumber = (reader: PathReader): number | null => {
    skipSeparators(reader);

    PATH_NUMBER_PATTERN.lastIndex = reader.index;

    const match = PATH_NUMBER_PATTERN.exec(reader.source);

    if(!match) {
        return null;
    }

    reader.index = PATH_NUMBER_PATTERN.lastIndex;

    return Number(match[0]);
};

const readFlag = (reader: PathReader): number | null => {
    skipSeparators(reader);

    const flag = reader.source.charAt(reader.index);

    if(flag !== "0" && flag !== "1") {
        return null;
    }

    reader.index += 1;

    return Number(flag);
};

const readParams = (reader: PathReader, count: number): Array<number> | null => {
    const params: Array<number> = [];

    for(let index = 0; index < count; index += 1) {
        const value = PATH_ARC_FLAG_INDEXES.includes(index) && count === PATH_PARAM_COUNTS.a ? readFlag(reader) : readNumber(reader);

        if(value === null) {
            return null;
        }

        params.push(value);
    }

    return params;
};

const getCubicLength = (from: Point, first: Point, second: Point, to: Point): number => {
    let length = 0;

    let previous = from;

    for(let step = 1; step <= PATH_CURVE_SAMPLE_COUNT; step += 1) {
        const t = step / PATH_CURVE_SAMPLE_COUNT;

        const inverse = 1 - t;

        const point: Point = [
            inverse ** 3 * from[0] + 3 * inverse ** 2 * t * first[0] + 3 * inverse * t ** 2 * second[0] + t ** 3 * to[0],
            inverse ** 3 * from[1] + 3 * inverse ** 2 * t * first[1] + 3 * inverse * t ** 2 * second[1] + t ** 3 * to[1]
        ];

        length += getDistance(previous, point);

        previous = point;
    }

    return length;
};

const getQuadraticLength = (from: Point, control: Point, to: Point): number => {
    return getCubicLength(from, [
        from[0] + (control[0] - from[0]) * 2 / 3,
        from[1] + (control[1] - from[1]) * 2 / 3
    ], [
        to[0] + (control[0] - to[0]) * 2 / 3,
        to[1] + (control[1] - to[1]) * 2 / 3
    ], to);
};

const getArcLength = (from: Point, params: Array<number>): number => {
    const [
        radiusXParam = 0,
        radiusYParam = 0,
        rotationParam = 0,
        largeArc = 0,
        sweep = 0,
        toX = 0,
        toY = 0
    ] = params;

    const to: Point = [
        toX,
        toY
    ];

    let radiusX = Math.abs(radiusXParam);

    let radiusY = Math.abs(radiusYParam);

    if(!radiusX || !radiusY || (from[0] === toX && from[1] === toY)) {
        return getDistance(from, to);
    }

    const rotation = rotationParam * Math.PI / 180;

    const cosine = Math.cos(rotation);

    const sine = Math.sin(rotation);

    const deltaX = (from[0] - toX) / 2;

    const deltaY = (from[1] - toY) / 2;

    const x1 = cosine * deltaX + sine * deltaY;

    const y1 = -sine * deltaX + cosine * deltaY;

    const scale = x1 ** 2 / radiusX ** 2 + y1 ** 2 / radiusY ** 2;

    if(scale > 1) {
        radiusX *= Math.sqrt(scale);

        radiusY *= Math.sqrt(scale);
    }

    const numerator = radiusX ** 2 * radiusY ** 2 - radiusX ** 2 * y1 ** 2 - radiusY ** 2 * x1 ** 2;

    const denominator = radiusX ** 2 * y1 ** 2 + radiusY ** 2 * x1 ** 2;

    const sign = largeArc === sweep ? -1 : 1;

    const factor = denominator ? sign * Math.sqrt(Math.max(numerator / denominator, 0)) : 0;

    const centerX1 = factor * radiusX * y1 / radiusY;

    const centerY1 = -factor * radiusY * x1 / radiusX;

    const centerX = cosine * centerX1 - sine * centerY1 + (from[0] + toX) / 2;

    const centerY = sine * centerX1 + cosine * centerY1 + (from[1] + toY) / 2;

    const startAngle = Math.atan2((y1 - centerY1) / radiusY, (x1 - centerX1) / radiusX);

    let deltaAngle = Math.atan2((-y1 - centerY1) / radiusY, (-x1 - centerX1) / radiusX) - startAngle;

    if(!sweep && deltaAngle > 0) {
        deltaAngle -= Math.PI * 2;
    }

    if(sweep && deltaAngle < 0) {
        deltaAngle += Math.PI * 2;
    }

    const sampleCount = Math.max(Math.ceil(Math.abs(deltaAngle) / Math.PI * PATH_CURVE_SAMPLE_COUNT), PATH_ARC_MIN_SAMPLE_COUNT);

    let length = 0;

    let previous = from;

    for(let step = 1; step <= sampleCount; step += 1) {
        const angle = startAngle + deltaAngle * step / sampleCount;

        const point: Point = [
            centerX + cosine * radiusX * Math.cos(angle) - sine * radiusY * Math.sin(angle),
            centerY + sine * radiusX * Math.cos(angle) + cosine * radiusY * Math.sin(angle)
        ];

        length += getDistance(previous, point);

        previous = point;
    }

    return length;
};

export const getAnimatedIconPathLength = ({
    d
}: {
    d: string;
}): number => {
    const reader: PathReader = {
        source: d,
        index: 0
    };

    let current: Point = [
        0,
        0
    ];

    let start: Point = [
        0,
        0
    ];

    let previousCubic: Point | null = null;

    let previousQuadratic: Point | null = null;

    let length = 0;

    let command = "";

    for(;;) {
        skipSeparators(reader);

        if(reader.index >= d.length) {
            break;
        }

        const character = d.charAt(reader.index);

        if(PATH_COMMAND_PATTERN.test(character)) {
            command = character;

            reader.index += 1;
        } else if(!command) {
            break;
        }

        const type = command.toLowerCase();

        const isRelative = command === type;

        if(type === "z") {
            length += getDistance(current, start);

            current = start;

            previousCubic = null;

            previousQuadratic = null;

            continue;
        }

        const params = readParams(reader, PATH_PARAM_COUNTS[type] ?? 0);

        if(!params) {
            break;
        }

        const param = (index: number) => params[index] ?? 0;

        const baseX = isRelative ? current[0] : 0;

        const baseY = isRelative ? current[1] : 0;

        let nextCubic: Point | null = null;

        let nextQuadratic: Point | null = null;

        let next: Point = current;

        if(type === "m" || type === "l") {
            next = [
                baseX + param(0),
                baseY + param(1)
            ];

            if(type === "l") {
                length += getDistance(current, next);
            } else {
                start = next;

                command = isRelative ? "l" : "L";
            }
        } else if(type === "h") {
            next = [
                baseX + param(0),
                current[1]
            ];

            length += getDistance(current, next);
        } else if(type === "v") {
            next = [
                current[0],
                baseY + param(0)
            ];

            length += getDistance(current, next);
        } else if(type === "c" || type === "s") {
            const offset = type === "c" ? 2 : 0;

            const first: Point = type === "c" ? [
                baseX + param(0),
                baseY + param(1)
            ] : [
                previousCubic ? 2 * current[0] - previousCubic[0] : current[0],
                previousCubic ? 2 * current[1] - previousCubic[1] : current[1]
            ];

            const second: Point = [
                baseX + param(offset),
                baseY + param(offset + 1)
            ];

            next = [
                baseX + param(offset + 2),
                baseY + param(offset + 3)
            ];

            length += getCubicLength(current, first, second, next);

            nextCubic = second;
        } else if(type === "q" || type === "t") {
            const control: Point = type === "q" ? [
                baseX + param(0),
                baseY + param(1)
            ] : [
                previousQuadratic ? 2 * current[0] - previousQuadratic[0] : current[0],
                previousQuadratic ? 2 * current[1] - previousQuadratic[1] : current[1]
            ];

            const offset = type === "q" ? 2 : 0;

            next = [
                baseX + param(offset),
                baseY + param(offset + 1)
            ];

            length += getQuadraticLength(current, control, next);

            nextQuadratic = control;
        } else if(type === "a") {
            next = [
                baseX + param(5),
                baseY + param(6)
            ];

            length += getArcLength(current, [
                ...params.slice(0, 5),
                next[0],
                next[1]
            ]);
        }

        previousCubic = nextCubic;

        previousQuadratic = nextQuadratic;

        current = next;
    }

    return length;
};

const getNumber = (value: string | number | undefined): number => {
    return Number(value ?? 0);
};

const getEllipsePerimeter = (radiusX: number, radiusY: number): number => {
    const sum = radiusX + radiusY;

    const ratio = ((radiusX - radiusY) / sum) ** 2;

    return Math.PI * sum * (1 + 3 * ratio / (10 + Math.sqrt(4 - 3 * ratio)));
};

const getPointsLength = (points: string, isClosed: boolean): number => {
    const values = points.split(/[\s,]+/).filter(Boolean).map(Number);

    let length = 0;

    for(let index = 2; index < values.length; index += 2) {
        length += getDistance([
            values[index - 2] ?? 0,
            values[index - 1] ?? 0
        ], [
            values[index] ?? 0,
            values[index + 1] ?? 0
        ]);
    }

    if(isClosed && values.length > 4) {
        length += getDistance([
            values[values.length - 2] ?? 0,
            values[values.length - 1] ?? 0
        ], [
            values[0] ?? 0,
            values[1] ?? 0
        ]);
    }

    return length;
};

export const getAnimatedIconNodeLength = ({
    attributes,
    tag
}: Pick<AnimatedIconNode, "attributes" | "tag">): number => {
    if(tag === "path") {
        return getAnimatedIconPathLength({
            d: String(attributes.d ?? "")
        });
    }

    if(tag === "line") {
        return getDistance([
            getNumber(attributes.x1),
            getNumber(attributes.y1)
        ], [
            getNumber(attributes.x2),
            getNumber(attributes.y2)
        ]);
    }

    if(tag === "circle") {
        return Math.PI * 2 * getNumber(attributes.r);
    }

    if(tag === "ellipse") {
        return getEllipsePerimeter(getNumber(attributes.rx), getNumber(attributes.ry));
    }

    if(tag === "polyline" || tag === "polygon") {
        return getPointsLength(String(attributes.points ?? ""), tag === "polygon");
    }

    const width = getNumber(attributes.width);

    const height = getNumber(attributes.height);

    const radiusX = Math.min(getNumber(attributes.rx ?? attributes.ry), width / 2);

    const radiusY = Math.min(getNumber(attributes.ry ?? attributes.rx), height / 2);

    return 2 * (width - 2 * radiusX) + 2 * (height - 2 * radiusY) + (radiusX && radiusY ? getEllipsePerimeter(radiusX, radiusY) : 0);
};
